import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import MessageBox from './MessageBox';

const Login = () => {
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const navigate = useNavigate();

  // ... your existing functions remain the same ...

  const checkUserRoleAndRedirect = useCallback(async (user) => {
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists() && userDoc.data().role === 'admin') {
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate("/homepage", { replace: true });
      }
    } catch (error) {
      console.error("Error checking user role:", error);
      navigate("/homepage", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        localStorage.setItem("username", user.displayName || user.email);
        checkUserRoleAndRedirect(user);
      } else {
        localStorage.removeItem("username");
      }
    });
    return () => unsubscribe();
  }, [checkUserRoleAndRedirect]);

  const showMessageBox = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
  };

  const closeMessageBox = () => {
    setMessage("");
    setMessageType("info");
  };

  const validateChristUniversityEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.christuniversity\.in$/.test(email?.toLowerCase() || "");

  const extractDepartmentFromEmail = (email) => {
    const match = email?.toLowerCase().match(/@([a-zA-Z0-9.-]+)\.christuniversity\.in$/);
    return match ? match[1] : null;
  };

  const handleGoogleSignIn = async () => {
    if (!registrationNumber.trim()) {
      showMessageBox("Please enter your registration number first!", "warning");
      return;
    }
    if (showAdminLogin && !adminCode.trim()) {
      showMessageBox("Please enter the admin code!", "warning");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (!validateChristUniversityEmail(user.email)) {
        await auth.signOut();
        showMessageBox(`Please use your official Christ University email. Your email: ${user.email}`, "danger");
        return;
      }

      const department = extractDepartmentFromEmail(user.email);

      if (showAdminLogin) {
        if (adminCode === 'CHRIST_ADMIN_2024') {
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            registrationNumber,
            department,
            role: 'admin',
            adminSince: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString()
          }, { merge: true });

          showMessageBox("Admin login successful!", "success");
          setTimeout(() => navigate("/admin/dashboard"), 1000);
        } else {
          showMessageBox("Invalid admin code. Please contact IT support.", "danger");
          return;
        }
      } else {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (!userDoc.exists()) {
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            registrationNumber,
            department,
            role: 'user',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString()
          });
        } else {
          await setDoc(doc(db, "users", user.uid), {
            lastLogin: new Date().toISOString(),
            registrationNumber,
            department
          }, { merge: true });
        }

        showMessageBox("Login successful!", "success");
        setTimeout(() => navigate("/homepage"), 1000);
      }

      const token = await user.getIdToken();
      localStorage.setItem("token", token);
      localStorage.setItem("username", user.displayName || user.email);
      localStorage.setItem("registrationNumber", registrationNumber);
      localStorage.setItem("department", department || "");
    } catch (error) {
      console.error("Google Sign-in Error:", error);
      const errMsg = error.code === 'auth/popup-closed-by-user'
        ? "Sign-in cancelled by user"
        : error.code === 'auth/admin-restricted-operation'
        ? "This account is restricted. Please contact IT support."
        : "Google Sign-in failed: " + error.message;

      showMessageBox(errMsg, "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center px-4"
      style={{ backgroundImage: "url('/StudentLoginBanner.jpg')" }}
    >
      <div className="absolute inset-0 bg-white opacity-40 z-0"></div>
      <div className="glass-card rounded-3xl px-6 py-8 space-y-6 w-full max-w-md">
        <div className="text-center space-y-2">
          <img src="/Christ_logo_latest.png" alt="Christ University Logo" className="max-w-[320px] mx-auto" />
          <h1 className="font-bold text-gray-900 text-3xl">Lost & Found CU</h1>
          <p className="text-gray-600 text-sm">Your campus companion for lost items.</p>
        </div>

        <div className="space-y-4">
          <div className="text-center">
            <h3 className="font-bold text-gray-900 text-lg">Welcome</h3>
            <p className="text-gray-700 text-sm">Sign in to get started.</p>
          </div>

          {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {/* Fixed: Proper label-input association with for/id attributes */}
            <div className="flex items-center gap-3">
  <label
    htmlFor="user-type-toggle"
    className="relative h-8 w-12 cursor-pointer [-webkit-tap-highlight-color:_transparent]"
  >
    <input
      id="user-type-toggle"
      name="user-type-toggle"
      type="checkbox"
      className="peer sr-only"
      checked={showAdminLogin}
      onChange={() => setShowAdminLogin(!showAdminLogin)}
      style={{ pointerEvents: 'auto', zIndex: 10, position: 'relative' }}
    />

    {/* Track */}
    <span className="absolute inset-0 m-auto h-2 rounded-full bg-blue-100"></span>

    {/* Toggle knob */}
    <span className="absolute inset-y-0 left-0 m-auto h-6 w-6 rounded-full bg-yellow-600 transition-all peer-checked:left-6 peer-checked:[&_>_*]:scale-0">
      <span className="absolute inset-0 m-auto h-4 w-4 rounded-full bg-blue-600 transition"></span>
    </span>
  </label>

  {/* Label text and icon */}
  <span className={`font-medium ${showAdminLogin ? "text-yellow-800" : "text-blue-900"}`}>
    <span className={`material-symbols-outlined mr-1 ${showAdminLogin ? "text-yellow-500" : "text-blue-600"}`}>
      {showAdminLogin ? "verified_user" : "person"}
    </span>
    {showAdminLogin ? "Administrator" : "Student"}
  </span>
</div>


            {/* Fixed: Proper label-input association */}
            <div className="text-left">
              <label htmlFor="registration-number" className="block text-sm font-medium text-gray-600 mb-1">
                {showAdminLogin ? 'Staff ID / Registration Number' : 'Registration Number'}
              </label>
              <input
                id="registration-number"
                name="registration-number"
                type="text"
                className="w-full py-3 px-4 border border-gray-200 rounded-lg bg-white/70 focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder={showAdminLogin ? "E.g., STAFF001 or 20BCE1234" : "E.g., 20BCE1234"}
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                required
                aria-describedby="registration-help"
                style={{ pointerEvents: 'auto', zIndex: 10, position: 'relative' }}
              />
            </div>

            {/* Fixed: Proper label-input association for admin code */}
            {showAdminLogin && (
              <div className="text-left">
                <label htmlFor="admin-code" className="block font-medium text-gray-600 mb-1 text-sm">
                  Admin Access Code
                </label>
                <input
                  id="admin-code"
                  name="admin-code"
                  type="password"
                  className="w-full py-3 px-4 border border-gray-200 rounded-lg bg-yellow-50 focus:border-yellow-400 focus:outline-none text-sm"
                  placeholder="Enter admin access code"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  required
                  aria-describedby="admin-code-help"
                  style={{ pointerEvents: 'auto', zIndex: 10, position: 'relative' }}
                />
                <small id="admin-code-help" className="text-yellow-600 text-xs">
                  Contact IT support if you don't have the admin code.
                </small>
              </div>
            )}

            <div id="registration-help" className="rounded bg-blue-50 border border-blue-200 text-blue-900 text-xs py-2 px-3">
              <span className="material-symbols-outlined align-middle mr-1 text-base text-blue-400 inline">info</span>
              Please use your official Christ University email.
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-pill font-semibold text-sm transition ${
                showAdminLogin ? "bg-yellow-500 hover:bg-yellow-600" : "bg-blue-600 hover:bg-blue-700"
              } ${loading ? "opacity-50 cursor-not-allowed" : "text-white"}`}
              disabled={loading}
              aria-describedby="sign-in-help"
               style={{ pointerEvents: loading ? 'none' : 'auto', zIndex: 10, position: 'relative' }}
            >
              {loading ? (
                <>
                  <svg className="animate-pulse h-5 w-5 mr-1" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="3" fill="none" />
                  </svg>
                  {showAdminLogin ? 'Authenticating Admin...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google {showAdminLogin && '(Admin)'}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
